/*@flow*/
/** @jsx jsx */
import {
  Fragment,
  useState,
  useEffect,
  createContext,
  useContext,
  useReducer,
  memo,
} from 'react';
import { jsx, css } from '@emotion/core';
import styled from '@emotion/styled';
import type {
  Category$Response,
  Category$SearchResponse,
  Carto$ErrorResponse,
} from 'lib/dataviews/types';
import ProgressBar from 'comps/App/ProgressBar';
import numeral from 'numeral';
import * as I from 'immutable';
import Blank from 'lib/Blank';
import Present from 'lib/Present';
import setIn from 'lib/setIn';
import hasProperty from 'lib/hasProperty';
import { type CartoResponse } from 'lib/getCartoMap';
import checkResponseStatus from 'lib/checkResponseStatus';
import Task from 'folktale/concurrency/task';
import { compose } from 'recompose';

type FilterChangeCallback = ({ accepted: string[] }) => any;
type Props = {
  id: string,
  title?: string,
  mapConfig: ?CartoResponse,
  accepted?: ?(string[]),
  onFiltersChanged?: FilterChangeCallback,
  numberFormat?: string,
};
const MODES = {
  NOT_READY: 'NOT_READY',
  READY: 'READY',
  SEARCH: 'SEARCH',
  SEARCHING: 'SEARCHING',
  LOCKED: 'LOCKED',
};
const FILTER_MODES = {
  IN_SYNC: 'IN_SYNC',
  OUT_OF_SYNC: 'OUT_OF_SYNC',
};
const PAGE_SIZE = 6;
type MODE = $Values<typeof MODES>;
type FILTER_MODE = $Values<typeof FILTER_MODES>;

let ReducerContext;
let PaginationContext;

const initialState = {
  mode: 'READY',
  filterMode: 'IN_SYNC',
  selected: [],
  __backup: [],
  locked: [],
  data: null,
  searchInput: '',
  searchResult: null,
  error: null,
  url: null,
  numberFormat: '0a',
};

const ActionType = {
  SELECTED: 'selected',
  REJECTED: 'rejected',
  LOCKED: 'locked',
  UNLOCKED: 'unlocked',
  SEARCH_REQUESTED: 'search_requested',
  SEARCH_PERFORMED: 'search_performed',
  FILTERS_APPLIED: 'filters_applied',
  SEARCH_CANCELED: 'search_canceled',
  AUTO_STYLE_APPLIED: 'auto_style_applied',
  AUTO_STYLE_REMOVED: 'auto_style_removed',
  SET_RECORD: 'set_record',
  RETRY_REQUESTED: 'retry_requested',
};
type Action = { type: $Values<typeof ActionType>, payload?: any };
type State = {
  mode: MODE,
  filterMode: FILTER_MODE,
  selected: string[],
  __backup: string[],
  locked: string[],
  data: ?Category$Response,
  searchInput: string,
  searchResult: ?Category$SearchResponse,
  error: ?Carto$ErrorResponse,
  url: ?string,
  numberFormat: ?string,
};

function reducer(state: State, action: Action) {
  const prevPOJOState = state;
  state = I.fromJS(prevPOJOState);
  const findNextFilterMode = v => {
    const inSearchMode = v.get('mode') === MODES.SEARCH;
    const selectedChanged = !I.is(v.get('selected'), v.get('__backup'));
    let fm = FILTER_MODES.IN_SYNC;

    if (inSearchMode && selectedChanged) {
      fm = FILTER_MODES.OUT_OF_SYNC;
    }
    return v.set('filterMode', fm);
  };

  const backupAccepted = v => v.set('__backup', v.get('selected'));
  const restoreAccepted = v => v.set('selected', v.get('__backup'));
  const clearBackup = v => v.update('__backup', v2 => v2.clear());
  const syncFilterMode = v => v.set('filterMode', FILTER_MODES.IN_SYNC);
  const setLockedItems = v => v.set('locked', v.get('selected'));
  const clearLockedItems = v => v.update('locked', v2 => v2.clear());
  const clearSelected = v => v.update('selected', v2 => v2.clear());
  const clearSearchArtefacts = v =>
    v.set('searchResult', null).set('searchInput', '');
  const clearErrors = v => v.set('error', null);

  switch (action.type) {
    case ActionType.SELECTED:
      state = state
        .update('selected', v => v.push(action.payload))
        .update(findNextFilterMode);
      break;
    case ActionType.REJECTED:
      state = state
        .update('selected', v => v.filter(v2 => v2 !== action.payload))
        .update(findNextFilterMode);
      break;
    case ActionType.SEARCH_REQUESTED:
      state = state
        .set('mode', MODES.SEARCH)
        .update(syncFilterMode)
        .update(backupAccepted);
      break;
    case ActionType.SEARCH_CANCELED:
      state = state
        .set('mode', MODES.READY)
        .update(syncFilterMode)
        .update(restoreAccepted)
        .update(clearSearchArtefacts)
        .update(clearErrors)
        .update(clearBackup);
      break;
    case ActionType.FILTERS_APPLIED:
      state = state
        .update(syncFilterMode)
        .update(clearBackup)
        .set('mode', MODES.READY);
      break;
    case ActionType.LOCKED:
      state = state.update(setLockedItems);
      break;
    case ActionType.UNLOCKED:
      state = state
        .update(clearLockedItems)
        .update(clearSearchArtefacts)
        .update(clearSelected);
      break;
    case ActionType.RETRY_REQUESTED:
      // TODO: load the data from server or perform search query,
      // whichever caused the error.
      break;
    case ActionType.SEARCH_PERFORMED:
      if (action.payload) {
        state = state.set('searchResult', action.payload).update(clearErrors);
      }
      break;
    case ActionType.SET_RECORD:
      if (action.payload) {
        const { path, value } = action.payload;
        state = setIn(state)({ path, value });
      }
      break;
    default:
      throw new Error('Unknown action type: ' + action.type);
  }

  // TODO: this should be the same object, not a new one...
  return state.toJS();
}

// const dummySearchResult = new Promise(res => {
//   setTimeout(() => {
//     const categories = I.Range(1, 20)
//       .map(v => ({ category: `foo-${v}`, value: v, group: false }))
//       .toJS();

//     res({
//       categories,
//       type: 'aggregation',
//     });
//   }, 0);
// });

const api = ctx => ({
  search: (q): ?Promise<Category$SearchResponse> => {
    if (!ctx.state.url) return;
    const url = `${ctx.state.url}/search?q=${q}`;
    const res = Task.fromPromised(() => fetchUrl<Category$SearchResponse>(url));
    return res()
      .run()
      .promise();
  },
});

const commands = ctx => {
  return {
    searchPerformed(result) {
      ctx.dispatch({ type: ActionType.SEARCH_PERFORMED, payload: result });
    },
    updateSearchValue(txt) {
      ctx.dispatch({
        type: ActionType.SET_RECORD,
        payload: { path: 'searchInput', value: txt },
      });
    },
    setError(err) {
      ctx.dispatch({
        type: ActionType.SET_RECORD,
        payload: { path: 'error', value: err },
      });
    },
    set(path, value) {
      ctx.dispatch({
        type: ActionType.SET_RECORD,
        payload: { path, value },
      });
    },
    tryAgain() {
      ctx.dispatch({ type: ActionType.RETRY_REQUESTED });
    },
    backupData(data) {
      if (hash(ctx.state.data) !== hash(data)) {
        ctx.dispatch({
          type: ActionType.SET_RECORD,
          payload: { path: 'data', value: data },
        });
      }
    },
    backupUrl(url) {
      ctx.dispatch({
        type: ActionType.SET_RECORD,
        payload: { path: 'url', value: url },
      });
    },
    showAll() {
      ctx.dispatch({ type: ActionType.UNLOCKED });
    },
    enterSearchMode() {
      ctx.dispatch({ type: ActionType.SEARCH_REQUESTED });
    },
    exitSearchMode() {
      ctx.dispatch({ type: ActionType.SEARCH_CANCELED });
    },
    applyFilters() {
      if (ctx.state.mode === MODES.SEARCH) {
        ctx.dispatch({ type: ActionType.LOCKED });
      }
      ctx.dispatch({ type: ActionType.FILTERS_APPLIED });
    },
    toggleItem(item: { category: string }) {
      const isSelected = ctx.state.selected.includes(item.category);
      const type = isSelected ? ActionType.REJECTED : ActionType.SELECTED;
      ctx.dispatch({ type, payload: item.category });
    },
  };
};

const ApplyBtn = () => {
  const ctx = useContext(ReducerContext);
  if (ctx.state.filterMode === FILTER_MODES.IN_SYNC) return null;

  return (
    <button
      css={css(`
    color: var(--blue);
    border: none;
    background: none;
    position: absolute;
    top: 6px;
    right: 0;
    font-size: 0.725rem;
  `)}
      onClick={() => {
        commands(ctx).applyFilters();
      }}
    >
      Apply
    </button>
  );
};

function delay(ms) {
  return new Promise(resolve => {
    setTimeout(() => resolve(ms), ms);
  });
}

const SearchInput = memo(props => {
  const ctx = useContext(ReducerContext);

  const onSearchResult = commands(ctx).searchPerformed;
  let task;

  useEffect(() => {
    return () => {
      task && task.cancel();
    };
  }, []);

  const performSearch = txt => {
    const promised = () =>
      delay(50).then(() => {
        const res = api(ctx).search(txt);
        res && res.then(onSearchResult);
      });

    const mkTask = Task.fromPromised(promised);
    task = mkTask()
      .orElse(reason => {
        commands(ctx).setError(reason);
      })
      .run();
  };

  return (
    <input
      placeholder={props.title ? `Search by ${props.title}` : 'Search...'}
      value={ctx.state.searchInput}
      onChange={e => {
        if (e) {
          const txt = e.target.value;
          commands(ctx).updateSearchValue(txt);
          performSearch(txt);
        }
      }}
      css={css(
        `
            border: none;
            outline: 0 !important;
            padding-top: 6px;
            width: 100%;
            height: 100%;
            line-height: normal;
          `
      )}
      autoFocus
    />
  );
});

const SearchHeader = props => {
  return (
    <div
      css={css(`
          display: grid;
          grid-template-columns: 22px 1fr;
          align-items: center;
          margin-bottom: 10px;
        `)}
    >
      <i
        css={css(
          `
            color: var(--blue);
            font-size: 0.825rem;
            text-align: center;
          `
        )}
        className="fa fa-search"
      />{' '}
      <div css={css('position: relative;')}>
        <SearchInput title={props.title} />
        <ApplyBtn />
      </div>
    </div>
  );
};

const ButtonLink = (props: { children: React$Node, onClick?: Function }) => {
  return (
    <button
      css={css(`
              text-transform: uppercase;
              font-size: 0.725rem;
              color: var(--blue);
              border: none; background: none;
              font-weight: lighter;
            `)}
      onClick={props.onClick}
    >
      {props.children}
    </button>
  );
};

const ShowAllButton = () => {
  const ctx = useContext(ReducerContext);
  if (ctx.state.mode === MODES.SEARCH) return null;
  const numSelected = ctx.state.selected.length;
  if (numSelected === 0) return null;
  return (
    <ButtonLink
      onClick={() => {
        commands(ctx).showAll();
        commands(ctx).applyFilters();
      }}
    >
      ALL
    </ButtonLink>
  );
};

const SelectionFeedback = () => {
  const ctx = useContext(ReducerContext);
  const numSelected = ctx.state.selected.length;
  const numLocked = ctx.state.locked.length;
  if (ctx.state.mode === MODES.SEARCH) return <b>{numSelected} selected</b>;

  if (numLocked === 0) {
    return <span>{numSelected === 0 ? 'All' : numSelected} selected</span>;
  }

  return <span>{numLocked} blocked </span>;
};

const Title = props =>
  props.children ? (
    <div css={css('font-size: 1rem; font-weight: bold; margin-bottom: 10px;')}>
      {props.children}
    </div>
  ) : null;

const PanelHeader = props => {
  const ctx = useContext(ReducerContext);
  const inSearchMode = ctx.state.mode === MODES.SEARCH;

  return (
    <div>
      {inSearchMode ? (
        <SearchHeader title={props.title} />
      ) : (
        <Title>{props.title}</Title>
      )}
      <div css={css('display: grid; grid-template-columns: 1fr 30px;')}>
        <div
          css={css(`
            text-transform: uppercase;
            font-size: 0.725rem;
            margin-bottom: 10px;
          `)}
        >
          <SelectionFeedback />
        </div>
        <div>
          <ShowAllButton />
        </div>
      </div>
    </div>
  );
};

const FooterButton = props => {
  return (
    <button
      css={css(`
          background: none;
          border: none;
          font-weight: lighter;

          text-transform: uppercase;
          color: var(--blue);
          font-size: 0.725rem;
          font-weight: normal;
          padding: 0;
        `)}
      onClick={props.onClick}
    >
      {props.children}
    </button>
  );
};

const SearchButton = () => {
  const ctx = useContext(ReducerContext);
  const data = ctx.state.data;
  const numCategories = data ? data.categoriesCount || data.count : 0;
  if (data && numCategories <= data.categories.length) return null;

  return (
    <FooterButton
      onClick={() => {
        commands(ctx).enterSearchMode();
      }}
    >
      <i className="fa fa-search" /> Search in {numCategories} categories
    </FooterButton>
  );
};

const CancelButton = () => {
  const ctx = useContext(ReducerContext);

  return (
    <FooterButton
      onClick={() => {
        commands(ctx).exitSearchMode();
      }}
    >
      Cancel
    </FooterButton>
  );
};

const PrevButton = props => {
  return (
    <ButtonLink onClick={props.onClick}>
      <i className="fa fa-caret-left" />
    </ButtonLink>
  );
};

const NextButton = props => {
  return (
    <ButtonLink onClick={props.onClick}>
      <i className="fa fa-caret-right" />
    </ButtonLink>
  );
};

const Muted = styled.span('color: var(--gray);');

const SearchPagination = () => {
  const ctx = useContext(ReducerContext);
  const results = ctx.state.searchResult;
  const { currentPage, setPage } = useContext(PaginationContext);
  if (Blank.is(results)) return null;
  if (Blank.is(results && results.categories)) return null;
  const categories = results ? results.categories : [];
  const numPages = Math.ceil(categories.length / PAGE_SIZE);
  const getPrevPage = () => {
    const np = currentPage - 1;
    return np < 0 ? numPages - 1 : np;
  };
  const getNextPage = () => {
    const np = currentPage + 1;
    const max = numPages - 1;
    return np > max ? 0 : np;
  };

  return (
    <div
      css={css(
        'text-align: right; font-size: 0.825rem; display: grid; align-items: center;'
      )}
    >
      <div>
        <PrevButton onClick={() => setPage(getPrevPage())} /> {currentPage + 1}{' '}
        <Muted>OF</Muted> {numPages}{' '}
        <NextButton onClick={() => setPage(getNextPage())} />
      </div>
    </div>
  );
};

const EmptySearchResult = () => {
  const ctx = useContext(ReducerContext);

  return (
    <div
      css={css('display: grid; justify-items: center; align-items: center;')}
    >
      <div>No results for your query "{ctx.state.searchInput}".</div>
    </div>
  );
};

const PanelFooter = () => {
  const ctx = useContext(ReducerContext);
  const inSearchMode = ctx.state.mode === MODES.SEARCH;

  return (
    <div
      css={css(`
        // margin-top: 10px;
        display: grid;
        grid-template-columns: ${inSearchMode ? '50px 1fr' : '1fr'};
    `)}
    >
      <div>{inSearchMode ? <CancelButton /> : <SearchButton />}</div>
      {inSearchMode && <SearchPagination />}
    </div>
  );
};

const keepUniqueCategories = categories => {
  const ret = {};
  if (categories) {
    categories.forEach(v => {
      ret[v.category] = v;
    });
  }

  return ((Object.values(ret): any): $PropertyType<
    Category$Response,
    'categories'
  >);
};

const isAggregated = item => {
  if (hasProperty(item, 'group')) {
    return item.group;
  } else if (hasProperty(item, 'agg')) {
    return item.agg;
  }
  return false;
};

const PanelBody = () => {
  const ctx = useContext(ReducerContext);
  const paginator = useContext(PaginationContext);
  const { searchResult, data } = ctx.state;
  const inSearchMode = ctx.state.mode === MODES.SEARCH;
  const somethingIsSelected = ctx.state.selected.length > 0;
  const locked = ctx.state.locked;
  const hasLockedItems = locked.length > 0;
  const searchCategories = searchResult ? searchResult.categories : [];
  const onlyLockedItems = v => {
    return hasLockedItems && locked.includes(v.category);
  };

  let categories;
  if (inSearchMode) {
    if (Present.is(searchCategories) && data) {
      categories = searchCategories
        .filter(onlyLockedItems)
        .concat(data.categories.filter(onlyLockedItems))
        .concat(searchCategories);
    } else if (data) {
      categories = data.categories;
    }
    if (categories) {
      categories = categories.filter(v => !isAggregated(v));
    }
  } else if (data) {
    if (Present.is(searchCategories)) {
      categories = searchCategories
        .filter(onlyLockedItems)
        .concat(data.categories.filter(onlyLockedItems))
        .concat(data.categories);
    } else {
      categories = data.categories;
    }
  }

  categories = keepUniqueCategories(categories);

  let items = I.List(categories)
    .skipWhile((_, i) => i < paginator.currentPage * PAGE_SIZE)
    .take(PAGE_SIZE)
    .map((item, i) => {
      let isGroup = isAggregated(item);
      const itemIsSelected = ctx.state.selected.includes(item.category);
      const itemIsNotSelected = !itemIsSelected;
      let deEmphasize = somethingIsSelected && itemIsNotSelected;
      if (inSearchMode) {
        deEmphasize = itemIsNotSelected;
      }

      return (
        <div
          css={css(`
        ${isGroup ? '' : 'cursor: pointer;'}
        margin-bottom: 10px;
        .progress-bar { opacity: 0.6; transition: all 100ms; }
        .bg-gray { background-color: gray; }
        &:hover { .progress-bar { opacity: 1; } }
        ${deEmphasize ? 'opacity: 0.4;' : ''}
      `)}
          key={i}
          onClick={() => {
            !isGroup && commands(ctx).toggleItem(item);
          }}
        >
          <div
            css={css(`
          display: grid;
          grid-template-columns: auto 1fr;
        `)}
          >
            <CategoryItem
              itemIsSelected={itemIsSelected}
              somethingIsSelected={somethingIsSelected}
              deEmphasize={deEmphasize}
              category={item.category}
              isGroup={isGroup}
              total={numeral(item.value)
                .format(ctx.state.numberFormat)
                .toUpperCase()}
            />
          </div>
          {false && (
            <div>
              <Bar
                isGroup={isGroup}
                deEmphasize={deEmphasize}
                value={item.value}
                category={item.category}
              />
            </div>
          )}
        </div>
      );
    });

  if (items.isEmpty() && ctx.state.mode === MODES.SEARCH) {
    return <EmptySearchResult />;
  }

  return <div>{items}</div>;
};

const CategoryItem = ({
  category,
  deEmphasize,
  isGroup,
  total,
  itemIsSelected,
  somethingIsSelected,
}) => {
  const name = String(category).toUpperCase();
  const ctx = useContext(ReducerContext);
  const inSearchMode = ctx.state.mode === MODES.SEARCH;
  const isChecked = inSearchMode ? itemIsSelected : !deEmphasize;
  const isIndeterminate = !somethingIsSelected;

  return (
    <Fragment>
      <div css={css('color: #333; font-size: 0.825rem;')}>
        {!isGroup && (
          <input
            type="checkbox"
            ref={e => {
              if (e) {
                e.indeterminate = isIndeterminate;
              }
            }}
            defaultChecked={isChecked}
            css={css('margin-right: 5px;')}
          />
        )}
        {name}
      </div>
      <div css={css('text-align: right; font-size: 0.825rem; color: #999;')}>
        {total}
      </div>
    </Fragment>
  );
};

const Bar = props => {
  const ctx = useContext(ReducerContext);
  const data = ctx.state.data;
  if (!data) return null;
  const itemPercent = Math.min((props.value / data.max) * 100, 100);
  let barClass = props.deEmphasize || props.isGroup ? 'bg-gray' : 'bg-success';

  return (
    <ProgressBar
      now={itemPercent}
      striped={props.isGroup}
      wrapperCss={css('height: 5px; border-radius: 0.05rem;')}
      barClass={barClass}
    />
  );
};

function getDataViewUrl({
  id,
  mapConfig,
}: {
  id: string,
  mapConfig: CartoResponse,
}): string {
  return mapConfig.metadata.dataviews[id].url.https;
}

function fetchUrl<R>(url: string): Promise<R> {
  return fetch(url)
    .then(checkResponseStatus)
    .then(res => res.json());
}

function getDataView(url: string): Promise<Category$Response> {
  return fetchUrl<Category$Response>(url);
}

type TaskExecution = {
  run(): any,
  map(Function): TaskExecution,
  chain<V>((any) => V): V,
};

const getDataViewTask: ({
  id: string,
  mapConfig: CartoResponse,
}) => TaskExecution = Task.fromPromised(
  compose(
    getDataView,
    getDataViewUrl
  )
);

const hash = o => I.fromJS(o || {}).hashCode();

// TODO: implement Auto Style
const Component = (props: Props) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const ctx = { state, dispatch };
  const [currentPage, setPage] = useState(0);
  const paginator = { currentPage, setPage };

  useEffect(() => {
    let taskExecution;
    if (!props.mapConfig) return;
    const { mapConfig } = props;
    // Load the data for the dataview
    const dataTask = getDataViewTask({
      id: props.id,
      mapConfig,
    }).map(data => {
      commands(ctx).backupData(data);
      return data;
    });

    // Backup the url
    const url = getDataViewUrl({ mapConfig, id: props.id });
    commands(ctx).backupUrl(url);

    if (!taskExecution) {
      taskExecution = dataTask.run();
    }

    return () => {
      if (taskExecution) {
        taskExecution.cancel();
        taskExecution = undefined;
      }
    };
  }, [hash(props.mapConfig)]);

  useEffect(() => {
    if (
      ctx.state.mode === MODES.READY &&
      hash(props.accepted) !== hash(ctx.state.selected)
    ) {
      const fn = props.onFiltersChanged;
      setTimeout(() => {
        fn && fn({ accepted: ctx.state.selected });
      }, 30);
    }
  }, [hash(ctx.state.selected), ctx.state.mode]);

  useEffect(() => {
    if (props.numberFormat) {
      commands(ctx).set('numberFormat', props.numberFormat);
    }
  }, []);

  useEffect(() => {
    if (hash(props.accepted) !== hash(ctx.state.selected)) {
      commands(ctx).set('selected', I.fromJS(props.accepted || []));
    }
  }, [hash(props.accepted)]);

  if (!props.mapConfig) return null;
  if (!ctx.state.data) return null;
  const noMinHeight = ctx.state.data.categoriesCount < PAGE_SIZE;
  ReducerContext = createContext(ctx);
  PaginationContext = createContext(paginator);

  return (
    <ReducerContext.Provider value={ctx}>
      <PaginationContext.Provider value={paginator}>
        <div
          className="Category"
          css={css(`
            ${noMinHeight ? '' : 'min-height: 265px;'}
            display: grid;
            grid-template-rows: 66px 1fr 1.4rem;
          `)}
        >
          <PanelHeader title={props.title} />
          <PanelBody />
          <PanelFooter />
        </div>
      </PaginationContext.Provider>
    </ReducerContext.Provider>
  );
};

export default Component;
